import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Attachment component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconAttachment extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M15.9392 3.11645C15.5327 2.95012 15.0971 2.86643 14.6579 2.87024C14.2186 2.87406 13.7846 2.96531 13.381 3.13868C12.9774 3.31205 12.6124 3.56406 12.3072 3.88001L12.2981 3.88941L4.16767 12.0188C4.16766 12.0188 4.16768 12.0188 4.16767 12.0188C3.1668 13.0197 2.60449 14.3772 2.60449 15.7927C2.60449 17.2081 3.16678 18.5656 4.16767 19.5665C5.16856 20.5674 6.52605 21.1297 7.94152 21.1297C9.35699 21.1297 10.7145 20.5674 11.7154 19.5665L21.6141 9.66684C21.907 9.37393 22.3819 9.37391 22.6748 9.66679C22.9677 9.95967 22.9677 10.4345 22.6748 10.7274L12.776 20.6271C11.4939 21.9093 9.75481 22.6297 7.94152 22.6297C6.12823 22.6297 4.3892 21.9094 3.10701 20.6272C1.82482 19.345 1.10449 17.606 1.10449 15.7927C1.10449 13.9794 1.82482 12.2403 3.10701 10.9582L11.233 2.83305C11.6756 2.37613 12.2044 2.01155 12.7889 1.76046C13.3755 1.50848 14.0064 1.37585 14.6448 1.3703C15.2832 1.36475 15.9164 1.4864 16.5072 1.72815C17.0981 1.9699 17.6349 2.32691 18.0864 2.77835C18.5378 3.22978 18.8948 3.7666 19.1366 4.35749C19.3783 4.94837 19.5 5.58149 19.4944 6.21989C19.4889 6.85829 19.3563 7.4892 19.1043 8.07579C18.8532 8.66027 18.4886 9.18909 18.0318 9.63164L9.89744 17.7705C9.3734 18.2768 8.67138 18.5569 7.94274 18.5507C7.21409 18.5445 6.51704 18.2523 6.00171 17.7371C5.48638 17.222 5.194 16.525 5.18756 15.7964C5.18111 15.0677 5.46111 14.3657 5.96725 13.8415L5.97629 13.8321L14.1068 5.69909C14.3996 5.40614 14.8745 5.40606 15.1674 5.69891C15.4604 5.99175 15.4605 6.46663 15.1676 6.75957L7.04235 14.8875C6.81193 15.1283 6.68455 15.4497 6.6875 15.7831C6.69047 16.1184 6.82503 16.4392 7.0622 16.6763C7.29938 16.9134 7.62018 17.0479 7.95553 17.0507C8.28895 17.0536 8.61022 16.9261 8.85095 16.6956L16.9846 8.55743C17.3006 8.25227 17.5527 7.88734 17.726 7.48375C17.8994 7.08016 17.9907 6.64609 17.9945 6.20685C17.9983 5.76762 17.9146 5.33203 17.7483 4.92549C17.582 4.51895 17.3363 4.1496 17.0257 3.83901C16.7151 3.52841 16.3458 3.28278 15.9392 3.11645Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-attachment') === undefined) {
  customElements.define('cat-icon-attachment', BlocksIconAttachment);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-attachment': BlocksIconAttachment;
  }
}
